import * as T from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { WEAPONS, type WeaponId, type TargetSnapshot } from '@shooting-range/shared';
import { box as plainBox, cylinder, material, label, batchStatic } from './world.js';
import { buildRaiderRifle } from './enemy-fire.js';

const box = (...args: Parameters<typeof plainBox>): T.Mesh => {
  const mesh = plainBox(...args); mesh.geometry.dispose();
  mesh.geometry = new RoundedBoxGeometry(...args[1], 2, Math.min(...args[1]) * 0.18);
  return mesh;
};

export interface WeaponModel { group: T.Group; slide: T.Group; flash: T.Mesh; light: T.PointLight; muzzle: T.Vector3 }
export function buildWeapon(id: WeaponId): WeaponModel {
  const group = new T.Group(), slide = new T.Group(); group.add(slide);
  const steel = material(0x46514b, 0.85, 0.28), black = material(0x17201d, 0.5, 0.45), polymer = material(0x4c5547, 0.05, 0.85), rubber = material(0x242e28, 0, 0.95);
  const accent = material(0xafb985, 0.55, 0.4), brass = material(0xb89b57, 0.85, 0.28);
  const barrel = (radius: number, length: number, x: number, y: number, z: number, mat = black): T.Mesh => {
    const part = cylinder(group, radius, length, [x, y, z], mat, 16); part.rotation.x = Math.PI / 2; return part;
  };
  let muzzleZ = -0.53;
  if (id === 'handgun') {
    box(slide, [0.115, 0.095, 0.42], [0, 0.035, -0.18], steel);
    box(group, [0.105, 0.075, 0.31], [0, -0.025, -0.12], black);
    box(group, [0.1, 0.23, 0.12], [0, -0.16, -0.015], polymer).rotation.x = -0.18;
    barrel(0.025, 0.4, 0, 0.04, -0.24);
    for (let i = 0; i < 6; i++) box(slide, [0.118, 0.045, 0.008], [0, 0.045, -0.04 - i * 0.017], black);
    box(slide, [0.016, 0.025, 0.025], [0, 0.094, -0.365], accent);
    for (const x of [-0.035, 0.035]) box(slide, [0.015, 0.025, 0.025], [x, 0.094, -0.02], accent);
    muzzleZ = -0.46;
  } else {
    const long = id === 'sniper'; const shotgun = id === 'shotgun';
    box(slide, [0.15, 0.14, 0.44], [0, 0.015, -0.1], steel);
    box(group, [0.14, 0.09, 0.38], [0, -0.08, -0.1], polymer);
    box(group, [0.11, 0.25, 0.11], [0, -0.19, 0.02], rubber).rotation.x = -0.25;
    box(group, [0.14, 0.15, 0.37], [0, -0.025, 0.3], polymer);
    box(group, [0.17, 0.2, 0.05], [0, -0.045, 0.5], rubber);
    box(group, [0.115, 0.3, 0.18], [0, -0.2, -0.22], black).rotation.x = -0.12;
    barrel(shotgun ? 0.04 : 0.024, long ? 0.92 : 0.64, 0, 0.055, long ? -0.85 : -0.6);
    box(group, [0.145, 0.13, 0.4], [0, 0.005, -0.49], shotgun ? polymer : black);
    if (shotgun) barrel(0.026, 0.56, 0, -0.045, -0.65);
    for (let i = 0; i < 10; i++) {
      box(group, [0.165, 0.015, 0.014], [0, 0.09, -0.27 - i * 0.036], steel);
      for (const side of [-1, 1]) box(group, [0.003, 0.022, 0.025], [side * 0.074, 0, -0.33 - i * 0.031], rubber);
    }
    if (long) {
      barrel(0.052, 0.39, 0, 0.19, -0.18);
      barrel(0.065, 0.07, 0, 0.19, -0.4, steel);
      barrel(0.058, 0.055, 0, 0.19, 0.04, rubber);
      barrel(0.047, 0.006, 0, 0.19, 0.07, material(0x354e49, 0.9, 0.1));
      for (const z of [-0.28, -0.06]) box(group, [0.045, 0.1, 0.04], [0, 0.11, z], steel);
      const bolt = cylinder(slide, 0.01, 0.15, [0.1, 0.02, 0.01], steel); bolt.rotation.z = Math.PI / 2;
    } else {
      box(group, [0.1, 0.016, 0.09], [0, 0.18, -0.16], steel);
      for (const side of [-1, 1]) box(group, [0.012, 0.09, 0.055], [side * 0.043, 0.135, -0.16], steel);
      const optic = new T.Mesh(new T.PlaneGeometry(0.068, 0.065), new T.MeshPhysicalMaterial({ color: 0x759689, transparent: true, opacity: 0.25, metalness: 0.4, roughness: 0.05, side: T.DoubleSide })); optic.position.set(0, 0.138, -0.14); group.add(optic);
      const dot = new T.Mesh(new T.SphereGeometry(0.0025, 8, 6), new T.MeshBasicMaterial({ color: 0xf47f5d })); dot.position.set(0, 0.145, -0.17); group.add(dot);
    }
    muzzleZ = long ? -1.34 : -0.95;
    barrel(0.04, 0.1, 0, 0.055, muzzleZ + 0.02, steel);
    box(slide, [0.006, 0.05, 0.1], [0.078, 0.025, -0.11], black);
    box(slide, [0.009, 0.019, 0.045], [0.082, 0.025, -0.12], brass);
  }
  const guard = new T.Mesh(new T.TorusGeometry(0.047, 0.007, 6, 16), black); guard.rotation.y = Math.PI / 2; guard.position.set(0, -0.08, -0.08); guard.scale.x = 0.75; group.add(guard);
  for (const side of [-1, 1]) for (const z of [-0.04, -0.22]) {
    const pin = cylinder(group, 0.006, 0.154, [0, 0.025, z], accent, 8); pin.rotation.z = Math.PI / 2;
  }
  // Sleeves and articulated gloves are entirely procedural.
  const sleeve = material(0x535c49), glove = material(0x555745), seam = material(0x333f33);
  const arm = box(group, [0.15, 0.14, 0.44], [0.015, -0.29, 0.22], sleeve); arm.rotation.x = -0.35;
  box(group, [0.135, 0.14, 0.16], [0.015, -0.185, 0.02], glove);
  for (let i = 0; i < 4; i++) {
    const finger = box(group, [0.13, 0.023, 0.043], [0.003, -0.13 - i * 0.028, -0.046], glove); finger.rotation.x = -0.15;
    box(group, [0.11, 0.005, 0.044], [0.003, -0.133 - i * 0.028, -0.048], seam);
  }
  if (id !== 'handgun') {
    const leftArm = box(group, [0.15, 0.14, 0.58], [-0.16, -0.24, -0.2], sleeve); leftArm.rotation.set(-0.3, -0.35, 0.2);
    box(group, [0.19, 0.1, 0.17], [-0.045, -0.09, -0.49], glove);
    for (let i = 0; i < 4; i++) box(group, [0.04, 0.1, 0.025], [0.075, -0.03, -0.43 - i * 0.032], glove);
  }
  const flash = new T.Mesh(new T.ConeGeometry(0.07, 0.35, 5), new T.MeshBasicMaterial({ color: 0xffd48b, transparent: true, opacity: 0.85, depthWrite: false }));
  flash.rotation.x = -Math.PI / 2; flash.position.set(0, 0.05, muzzleZ - 0.1); flash.visible = false; group.add(flash);
  const light = new T.PointLight(0xffca80, 0, 5, 2); light.position.copy(flash.position); group.add(light);
  batchStatic(group); batchStatic(slide);
  group.scale.setScalar(0.9);
  group.userData.weaponId = WEAPONS[id].id;
  return { group, slide, flash, light, muzzle: new T.Vector3(0, 0.05, muzzleZ) };
}

export function buildEnemy(kind: 'runner' | 'brute' | 'crawler' | 'husk' | 'raider', friendly = false): T.Group {
  const group = new T.Group(); const humanoid = kind === 'raider';
  if (kind === 'crawler' || kind === 'husk' || kind === 'brute') {
    const limbs: T.Group[] = [], crawler = kind === 'crawler', husk = kind === 'husk';
    const skin = material(crawler ? 0x8b493d : husk ? 0x999eaa : 0x615147, 0, 0.9);
    const plate = material(crawler ? 0x382d32 : husk ? 0x39314d : 0x39352f, 0.15, 0.85);
    const bone = material(crawler ? 0xdfbd83 : husk ? 0xc5bdcf : 0xa58b63);
    const glow = material(crawler ? 0xffcf38 : husk ? 0x68e8f4 : 0xe54436);
    glow.emissive.copy(glow.color); glow.emissiveIntensity = 2;
    if (crawler) {
      // Low horizontal spine, broad splayed legs and a forward jaw.
      box(group, [0.55, 0.34, 0.78], [0, 0, 0.02], skin);
      for (const z of [-0.15, 0.08, 0.31]) box(group, [0.59, 0.12, 0.15], [0, 0.19, z], plate);
      box(group, [0.43, 0.23, 0.3], [0, -0.04, -0.43], skin);
      box(group, [0.39, 0.07, 0.16], [0, -0.17, -0.5], bone);
      for (const side of [-1, 1]) {
        box(group, [0.13, 0.075, 0.04], [side * 0.13, 0.02, -0.59], glow);
        for (const z of [-0.25, 0.28]) {
          const limb = new T.Group(); limb.position.set(side * 0.28, -0.05, z); group.add(limb);
          box(limb, [0.34, 0.13, 0.17], [side * 0.12, -0.07, 0], skin).rotation.z = -side * 0.4;
          box(limb, [0.12, 0.28, 0.13], [side * 0.27, -0.25, 0], plate);
          box(limb, [0.16, 0.06, 0.27], [side * 0.28, -0.46, -0.07], bone); limbs.push(limb);
        }
      }
    } else if (husk) {
      // Narrow rib cage and open hood; long sleeves hang below the ragged mantle.
      box(group, [0.36, 0.83, 0.27], [0, 0.12, 0], skin);
      box(group, [0.7, 0.18, 0.4], [0, 0.58, 0.04], plate);
      box(group, [0.46, 0.15, 0.42], [0, 1.02, 0], plate);
      for (const side of [-1, 1]) box(group, [0.12, 0.43, 0.4], [side * 0.22, 0.82, 0], plate);
      box(group, [0.27, 0.29, 0.16], [0, 0.82, 0.03], bone);
      for (const x of [-0.065, 0.065]) box(group, [0.045, 0.065, 0.025], [x, 0.86, -0.06], glow);
      box(group, [0.19, 0.32, 0.09], [0, 0.23, -0.17], glow);
      for (let i = 0; i < 5; i++) {
        const length = 0.55 + (i % 3) * 0.11;
        box(group, [0.13, length, 0.08], [(i - 2) * 0.13, -0.2 - length / 2, 0.19], plate).rotation.z = (i - 2) * 0.08;
      }
      for (const side of [-1, 1]) {
        const leg = new T.Group(); leg.position.set(side * 0.13, -0.32, 0); group.add(leg);
        box(leg, [0.12, 0.7, 0.14], [0, -0.35, 0], skin);
        box(leg, [0.17, 0.12, 0.29], [0, -0.77, -0.05], plate); limbs.push(leg);
        const arm = new T.Group(); arm.position.set(side * 0.36, 0.5, 0); group.add(arm);
        box(arm, [0.15, 1.12, 0.17], [side * 0.06, -0.53, 0], plate).rotation.z = side * 0.1;
        for (let i = 0; i < 3; i++) box(arm, [0.035, 0.25, 0.04], [side * 0.12 + (i - 1) * 0.05, -1.17, -0.04], bone);
        limbs.push(arm);
      }
    } else {
      // Forward head below a plated hump, with massive hanging fists.
      box(group, [1.12, 1.02, 0.7], [0, 0.08, 0.04], skin).rotation.x = -0.18;
      for (let i = 0; i < 3; i++) box(group, [1.25 - i * 0.16, 0.25, 0.67], [0, 0.46 + i * 0.15, 0.15 + i * 0.06], plate);
      box(group, [0.5, 0.41, 0.4], [0, 0.35, -0.48], skin);
      box(group, [0.48, 0.14, 0.27], [0, 0.13, -0.56], bone);
      for (const side of [-1, 1]) {
        box(group, [0.12, 0.07, 0.035], [side * 0.14, 0.39, -0.69], glow);
        cylinder(group, 0.09, 0.48, [side * 0.53, 0.86, -0.06], bone, 5).rotation.z = -side * 0.5;
        const leg = new T.Group(); leg.position.set(side * 0.31, -0.4, 0); group.add(leg);
        box(leg, [0.36, 0.65, 0.38], [0, -0.3, 0], skin);
        box(leg, [0.44, 0.22, 0.57], [0, -0.74, -0.1], plate); limbs.push(leg);
        const arm = new T.Group(); arm.position.set(side * 0.72, 0.4, 0); group.add(arm);
        box(arm, [0.46, 0.76, 0.44], [side * 0.04, -0.32, -0.08], skin);
        box(arm, [0.58, 0.4, 0.53], [side * 0.05, -0.86, -0.12], plate); limbs.push(arm);
      }
    }
    batchStatic(group); limbs.forEach(limb => batchStatic(limb));
    group.userData.limbs = limbs; return group;
  }
  const skin = material(friendly ? 0x7c8c68 : humanoid ? 0x525449 : 0x525e48, 0, 0.9);
  const armor = material(friendly ? 0x475e4a : 0x333d33, 0.4, 0.65);
  const eye = new T.MeshBasicMaterial({ color: friendly ? 0xb2df9b : 0xff895e });
  const bone = material(0xb7b49b);
  const sphere = (radius: number, position: [number, number, number], mat: T.Material, scale: [number, number, number] = [1, 1, 1]) => {
    const mesh = new T.Mesh(new T.IcosahedronGeometry(radius, 1), mat); mesh.position.set(...position); mesh.scale.set(...scale); mesh.castShadow = true; group.add(mesh); return mesh;
  };
  sphere(0.42, [0, 0.1, 0], skin, [1, 1.3, 0.65]);
  sphere(0.29, [0, 0.67, -0.05], skin, [0.8, 1, 0.8]);
  sphere(0.14, [0, 0.53, -0.23], armor, [1.25, 0.7, 0.8]);
  for (const x of [-0.09, 0.09]) sphere(0.042, [x, 0.68, -0.258], eye, [1.3, 0.55, 0.7]);
  const limbs: T.Group[] = [];
  for (const side of [-1, 1]) {
    const leg = new T.Group(); leg.position.set(side * 0.2, -0.26, 0); group.add(leg);
    box(leg, [0.19, 0.57, 0.2], [0, -0.24, 0], skin);
    box(leg, [0.23, 0.17, 0.33], [0, humanoid ? -0.56 : -0.60, -0.07], armor); limbs.push(leg);
    const arm = new T.Group(); arm.position.set(side * 0.38, 0.33, 0); group.add(arm);
    box(arm, [0.18, 0.58, 0.19], [side * 0.08, -0.24, -0.04], skin).rotation.z = side * 0.2;
    sphere(0.21, [side * 0.38, 0.35, 0], armor);
    if (!humanoid) {
      for (let i = 0; i < 3; i++) box(arm, [0.035, 0.2, 0.035], [side * 0.12 + (i - 1) * 0.055, -0.56, -0.06], bone).rotation.x = -0.4;
      for (let i = 0; i < 3; i++) {
        const spike = new T.Mesh(new T.ConeGeometry(0.075, 0.32, 5), bone); spike.position.set(side * (0.26 + i * 0.08), 0.42 - i * 0.08, 0.11); spike.rotation.z = -side * 0.5; group.add(spike);
      }
    }
    limbs.push(arm);
  }
  if (humanoid) {
    box(group, [0.65, 0.55, 0.3], [0, 0.14, -0.03], armor);
    sphere(0.31, [0, 0.73, -0.015], armor, [0.9, 0.7, 0.95]);
    if (friendly) box(group, [0.08, 0.1, 0.63], [0.3, 0.02, -0.42], armor);
    else { const rifle = buildRaiderRifle(); group.add(rifle.group); group.userData.rifle = rifle; }
    for (const x of [-0.18, 0, 0.18]) box(group, [0.14, 0.2, 0.09], [x, 0.1, -0.23], skin);
    if (!friendly) {
      box(group, [0.56, 0.13, 0.09], [0, 0.4, -0.25], material(0xbd7b50));
      box(group, [0.29, 0.06, 0.04], [0, 0.68, -0.28], eye);
    }
  }
  batchStatic(group); limbs.forEach(limb => batchStatic(limb));
  group.userData.limbs = limbs; return group;
}

export function buildTarget(target: TargetSnapshot): T.Group {
  if (target.kind === 'runner' || target.kind === 'brute' || target.kind === 'crawler' || target.kind === 'husk' || target.kind === 'raider') return buildEnemy(target.kind);
  const group = new T.Group(), metal = material(0x56665a, 0.7, 0.38);
  const disc = new T.Mesh(new T.CylinderGeometry(target.radius, target.radius, 0.065, 32), target.kind === 'steel' ? material(0xb1b8a0, 0.85, 0.25) : material(0xe1d7ba));
  disc.rotation.x = Math.PI / 2; group.add(disc);
  if (target.kind !== 'steel') {
    for (const [size, color] of [[0.79, 0x303a31], [0.6, 0xcfc7ac], [0.4, 0x303a31], [0.19, 0xc97650]] as const) {
      const ring = new T.Mesh(new T.CircleGeometry(target.radius * size, 32), new T.MeshStandardMaterial({ color, side: T.DoubleSide })); ring.position.z = 0.037 + (1 - size) * 0.005; group.add(ring);
    }
  }
  box(group, [0.045, 2, 0.045], [0, -1, 0], metal);
  box(group, [0.8, 0.12, 0.5], [0, -1.9, 0], metal);
  if (target.kind === 'popup') {
    const number = new T.Mesh(new T.PlaneGeometry(0.4, 0.1), new T.MeshBasicMaterial({ map: label('LIVE') })); number.position.set(0, -target.radius * 0.7, 0.05); group.add(number);
  }
  return group;
}
